import { createSelector } from '@reduxjs/toolkit';
import {
  selectEmployerTypeProjects,
  selectProjectsEntitiesWithJoinedData,
  selectSelectedProject,
  selectSelectedProjectId,
} from 'store/projects/selectors/projectsSelectors';

import { groupBy, orderBy, sortBy, uniq, uniqBy, uniqWith } from 'lodash-es';
import { selectUserAdminBusinessIds } from 'scenes/Permissions/store/selectors/base-project-permissions.selectors';
import { selectAdminEntities } from 'scenes/Permissions/store/selectors/permissionsSelectors';
import { selectWorkCalendarResourcesArray } from 'scenes/WorkDemo/store/selectors/base-work-calendar.selectors';
import { uniqueResourcesByUserAndEmployer } from 'scenes/WorkDemo/utils/uniqueResourcesByUserAndEmployer';
import {
  selectUserActiveEmployerBusinessId,
  selectUserActiveEmployerInfo,
} from 'store/user/selectors.js/user.selectors';
import { arrayToMap } from 'utils/arrayToMap';
import sort from 'utils/sort';
import {
  selectAllResources,
  selectAllResourcesToMakeAdminOf,
} from '../resourceSliceSelectors';
export const selectResourcesState = (state) => state.resources;

export const selectIsResourcesLoading = createSelector(
  [selectResourcesState],
  (state) => state.loading
);

export const selectIsResourceDeletePending = createSelector(
  [selectResourcesState],
  (state) => state.deleteResourcePending
);

export const selectResourceDeleteError = createSelector(
  [selectResourcesState],
  (state) => state.deleteResourceError
);

export const selectLoadedResourcesProjectIds = createSelector(
  [selectResourcesState],
  (state) => state.loadedResourcesProjectIds
);

// New implementation of storing logged in user resources, where they are seperated from all other resources. Used to make app faster, where user own resources doesn't need to be always filtered from huge amounts of resources data.
export const selectLoggedInUserResourcesEntities = createSelector(
  [selectResourcesState],
  (state) => state.loggedInUserResources.entities
);

export const selectIsJoiningEmployeeToProject = createSelector(
  [selectResourcesState],
  (state) => state.isJoiningEmployeeToProject
);

export const selectLoggedInUserResourcesArray = createSelector(
  [selectLoggedInUserResourcesEntities],
  (resourceEntities) =>
    Object.values(resourceEntities).filter((resource) => !resource.deletedAt)
);

export const selectLoggedInUserResourcesFromSelectedActiveEmployer =
  createSelector(
    [selectLoggedInUserResourcesArray, selectUserActiveEmployerBusinessId],
    (resources, activeEmployerBusinessId) => {
      if (activeEmployerBusinessId === 'myLixani') {
        return resources.filter((resource) => !resource.employer);
      } else {
        return activeEmployerBusinessId
          ? resources.filter(
              (resource) =>
                resource.employerBusinessId === activeEmployerBusinessId
            )
          : resources;
      }
    }
  );

export const selectCurrentUserResidentResources = createSelector(
  [selectLoggedInUserResourcesArray],
  (resources) =>
    resources.filter((resource) => resource.employmentType === 'RESIDENT')
);

export const selectSelectedProjectCurrentUserResources = createSelector(
  [selectLoggedInUserResourcesArray, selectSelectedProjectId],
  (resources, selectedProjectId) =>
    resources
      ? resources.filter((resource) => resource.project === selectedProjectId)
      : []
);

export const selectCurrentUserResourcesByProjectId = (projectId) =>
  createSelector([selectLoggedInUserResourcesArray], (resources) =>
    resources
      ? resources.filter((resource) => resource.project === projectId)
      : []
  );

export const selectSelectedProjectResources = createSelector(
  [selectAllResources, selectSelectedProjectId],
  (resources, selectedProjectId) =>
    resources
      ? resources.filter((resource) => resource.project === selectedProjectId)
      : []
);

export const selectSelectedProjectResourcesGroupedByEmployerBusinessId =
  createSelector([selectSelectedProjectResources], (resources) => {
    const uniqUserProjectResources = uniqWith(
      resources,
      (a, b) =>
        a.user === b.user && a.employerBusinessId === b.employerBusinessId
    );

    return groupBy(uniqUserProjectResources, 'employerBusinessId');
  });

export const selectSelectedProjectResourcesByEmployerBusinessId =
  createSelector(
    [
      selectSelectedProjectResourcesGroupedByEmployerBusinessId,
      (_, args) => args,
    ],
    (resourcesGroupedByBusinessId, args) => {
      const businessId = args;
      return resourcesGroupedByBusinessId[businessId];
    }
  );

export const selectResourcesArrayWithJoinedData = createSelector(
  [selectAllResources, selectProjectsEntitiesWithJoinedData],
  (resources, projects) =>
    resources.map((resource) => ({
      ...resource,
      employer: projects[resource.employer]
        ? projects[resource.employer]
        : { id: resource.employer },
      project: projects[resource.project]
        ? projects[resource.project]
        : { id: resource.project },
    }))
);

export const selectProjectResources = createSelector(
  [selectAllResources, selectWorkCalendarResourcesArray, (_, args) => args],
  (resources, workCalendarResources, args) => {
    const projectId = args;
    {
      let resourceOptions = uniqueResourcesByUserAndEmployer(
        [...resources, ...workCalendarResources].filter(
          (resource) => resource.project === projectId
        )
      );

      resourceOptions.sort((a, b) => sort.sortByKey(a, b, 'lastName'));

      return resourceOptions;
    }
  }
);

// selector filters out resource if resources project is created by employer.
export const selectCurrentProjectsResourcesArrayWithJoinedData = createSelector(
  [selectSelectedProjectCurrentUserResources],
  (resources) => {
    return resources.filter((resource) => {
      // cannot leave own employer projects through here
      const isProjectSameAsEmployer = resource.employer === resource.project;
      return !isProjectSameAsEmployer;
    });
  }
);

export const selectResourcesEntitiesWithJoinedData = createSelector(
  [selectResourcesArrayWithJoinedData],
  (resources) => arrayToMap(resources)
);

export const selectIsUserProjectResourcesLoaded = createSelector(
  [selectResourcesState],
  (state) => state.isUserProjectResourcesLoaded
);

export const selectCurrentUserResourcesIndexedByProject = createSelector(
  [selectLoggedInUserResourcesArray],
  (resources) => groupBy(resources, 'project')
);

export const selectCurrentUserResourcesFromActiveEmployerIndexedByProject =
  createSelector(
    [selectLoggedInUserResourcesFromSelectedActiveEmployer],
    (resources) => groupBy(resources, 'project')
  );

export const selectLoadingResourcesToMakeAdminOf = createSelector(
  [selectResourcesState],
  (state) => state.loadingResourcesToMakeAdminOf
);

export const selectResourcesToMakeAdminOf = createSelector(
  [selectAllResourcesToMakeAdminOf, selectAdminEntities, (_, args) => args],
  (resources, adminEntities, args) => {
    const businessId = args;
    if (!businessId) {
      return;
    }
    const resourcesUniqueByUser = uniqBy(resources, 'user');
    return resourcesUniqueByUser.map((resource) => {
      console.log(resource.user, businessId);
      return adminEntities[`${resource.user}#${businessId}`]
        ? { ...resource, isAdmin: true }
        : resource;
    });
  }
);

export const selectCurrentUserEmployerProjects = createSelector(
  [selectEmployerTypeProjects, selectLoggedInUserResourcesArray],
  (projects, userResources) => {
    const userResourceEmployerIds = uniq(
      userResources.map((resource) => resource.employer)
    );

    const userEmployerProjects = projects.filter((project) =>
      userResourceEmployerIds.includes(project.id)
    );

    return userEmployerProjects;
  }
);

export const selectCurrentUserActiveEmployerProject = createSelector(
  [selectCurrentUserEmployerProjects, selectUserActiveEmployerInfo],
  (employerProjects, activeEmployerInfo) => {
    const { activeEmployerId, activeEmployerBusinessId } = activeEmployerInfo;

    const foundActiveEmployer = activeEmployerId
      ? employerProjects.find(
          (project) =>
            project.id === activeEmployerId ||
            project.business_id === activeEmployerBusinessId
        )
      : undefined;

    return foundActiveEmployer;
  }
);

export const selectEmployeeResourcesOfSelectedEmployerBusinessByAdminStatus =
  createSelector(
    [selectAllResources, selectSelectedProject, selectAdminEntities],
    (resources, selectedProject, adminEntities) => {
      const projectBusinessId = selectedProject.business_id;

      const resourcesEmployedByBusiness = resources.filter(
        (resource) =>
          resource.employerBusinessId === projectBusinessId ||
          resource.employer === selectedProject.id
      );

      const uniqueUserResources = uniqBy(resourcesEmployedByBusiness, 'user');

      let adminResources = [];
      let normalResources = [];

      uniqueUserResources.forEach((resource) => {
        if (adminEntities[`${resource.user}#${projectBusinessId}`]) {
          adminResources.push(resource);
        } else {
          normalResources.push(resource);
        }
      });

      return {
        adminResources: sortBy(adminResources, ['firstName']),
        normalResources: sortBy(normalResources, ['firstName']),
      };
    }
  );

export const selectResourcesInProjectsOfBusinessId = createSelector(
  [selectAllResources, selectEmployerTypeProjects, (_, args) => args],
  (resources, employerProjects, businessId) => {
    const businessResources = resources.filter(
      (resource) =>
        resource.projectBusinessId === businessId && resource.employer
    );

    const resourcesByUniqueUserAndEmployerBusinessId = uniqBy(
      businessResources,
      (resource) => [resource.user, resource.employerBusinessId].join()
    );

    const formattedResourceOptions =
      resourcesByUniqueUserAndEmployerBusinessId.map((resource) => {
        const resourceEmployer = employerProjects.find(
          (employerProject) =>
            employerProject.business_id === resource.employerBusinessId
        );

        return {
          userId: resource.user,
          employerBusinessId: resource.employerBusinessId,
          name: `${resource.firstName} ${resource.lastName}`,
          employerName: resourceEmployer?.name,
        };
      });

    const nameSortedResources = orderBy(
      formattedResourceOptions,
      ['employerName', 'name'],
      'asc'
    );

    const groupedResources = groupBy(nameSortedResources, 'employerName');

    return groupedResources;
  }
);

export const selectIsResourceToSelectedProject = createSelector(
  [selectSelectedProjectId, selectCurrentUserResourcesIndexedByProject],
  (selectedProjectId, resources) =>
    resources[selectedProjectId] ? true : false
);

export const selectUserAdminBusinessResources = createSelector(
  [
    selectAllResources,
    selectWorkCalendarResourcesArray,
    selectUserAdminBusinessIds,
  ],
  (resources, workCalendarResources, userAdminBusinessIds) => {
    const adminBusinessResources = [
      ...resources,
      ...workCalendarResources,
    ].filter((resource) =>
      userAdminBusinessIds.includes(resource.employerBusinessId)
    );

    return sortBy(adminBusinessResources, 'firstName');
  }
);

export const selectAllResourcesWithProjectBusinessId = createSelector(
  [selectResourcesState],
  (state) => {
    const employers = state.allResourcesWithProjectBusinessId;

    if (!employers || !Array.isArray(employers)) {
      return [];
    }

    return orderBy(employers, ['name'], ['asc']);
  }
);

export const selectLoadingAllResourcesWithProjectBusinessId = createSelector(
  [selectResourcesState],
  (state) => state.loadingAllResourcesWithProjectBusinessId
);
